import { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import stateCityData from "../../data/states.json";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser, faEnvelope, faPhoneAlt, faIdCard, faCalendar, faTint,
  faMapMarkerAlt, faCity, faEye, faEyeSlash, faVenusMars, faImage, faBriefcase, faGraduationCap, faGavel, faLock
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";



const AdvocateRegister = () => {

  const [showOtpFields, setShowOtpFields] = useState(false);
  const [timer, setTimer] = useState(180);
  const [isResending, setIsResending] = useState(false);
  const [canResendOTP, setCanResendOTP] = useState(false);
  const [otpData, setOtpData] = useState({ otpEmail: "" });
  const navigate = useNavigate();



  const [formData, setFormData] = useState({
    name: "",
    gender: "",
    email: "",
    phoneNo: "",
    password: "",
    confirmPassword: "",
    aadharNo: "",
    dateOfBirth: "",
    pancard: "",
    residenceAddress: "",
    state: "",
    city: "",
    pincode: "",
    profileImage: null,
    specialization: "",
    experience: "",
    barAssociationCourt: "",
    uniqueId: "",
    termsAccepted: false
  });

  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Use JSON structure: { state: [cities] }
  const [states] = useState(Object.keys(stateCityData));
  const [filteredCities, setFilteredCities] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');



  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (name === "phoneNo") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setFormData({ ...formData, [name]: digitsOnly });
    } else if (name === "aadharNo") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 12);
      setFormData({ ...formData, [name]: digitsOnly });
    } else if (name === "name") {
      const lettersOnly = value.replace(/[^A-Za-z\s]/g, "");
      setFormData({ ...formData, [name]: lettersOnly });
    } else if (name === "pancard") {
      let pancardValue = value.toUpperCase().replace(/[^0-9A-Z]/g, "");
      pancardValue = pancardValue.slice(0, 10);
      setFormData({ ...formData, [name]: pancardValue });
    } else if (name === "pincode") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 6);
      setFormData({ ...formData, [name]: digitsOnly });
    } else if (name === "profileImage") {
      const file = files[0];
      if (file) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          setFormError("Please upload a valid image file");
          return;
        }
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          setFormError("Image size should not exceed 5MB");
          return;
        }
        setFormData({ ...formData, [name]: file });
        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setProfileImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    } else if (name === "termsAccepted") {
      setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    setFormError("");
  };

  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    setFormData({
      ...formData,
      state: selectedState,
      city: ""
    });
    // Set cities for selected state
    if (selectedState && Array.isArray(stateCityData[selectedState])) {
      setFilteredCities(stateCityData[selectedState]);
    } else {
      setFilteredCities([]);
    }
  }

  const validateForm = () => {
    // List of required fields and their labels
    const requiredFields = [
      { key: 'name', label: 'Full Name' },
      { key: 'gender', label: 'Gender' },
      { key: 'email', label: 'Email' },
      { key: 'phoneNo', label: 'PhoneNo' },
      { key: 'password', label: 'Password' },
      { key: 'confirmPassword', label: 'Confirm Password' },
      { key: 'aadharNo', label: 'Aadhar Number' },
      { key: 'dateOfBirth', label: 'Date of Birth' },
      { key: 'pancard', label: 'PAN Card' },
      { key: 'residenceAddress', label: 'Residence Address' },
      { key: 'state', label: 'State' },
      { key: 'city', label: 'City' },
      { key: 'pincode', label: 'PinCode' },
      { key: 'specialization', label: 'Specialization' },
      { key: 'experience', label: 'Experience' },
      { key: 'barAssociationCourt', label: 'Bar Association/Court' },
      { key: 'uniqueId', label: 'Unique ID (Advocate)' },
      { key: 'termsAccepted', label: 'Terms and Conditions' },
    ];
    const missingFields = requiredFields.filter(f => {
      if (f.key === 'termsAccepted') {
        return !formData[f.key];
      }
      return !formData[f.key] || formData[f.key].toString().trim() === '';
    });

    // Check profile image separately
    if (!formData.profileImage) {
      missingFields.push({ key: 'profileImage', label: 'Profile Image' });
    }

    if (missingFields.length > 0) {
      const missingLabels = missingFields.map(f => f.label).join(', ');
      setFormError(`Please fill the following field${missingFields.length > 1 ? 's' : ''}: ${missingLabels}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    // Name: only letters and spaces
    const nameRegex = /^[A-Za-z\s]+$/;
    if (!nameRegex.test(formData.name)) {
      setFormError("Name must contain only letters and spaces (no numbers allowed)");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    // Email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormError("Please enter a valid email address");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    // Only allow @gmail.com
    if (!formData.email.endsWith('@gmail.com')) {
      setFormError("Only @gmail.com email addresses are allowed");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    // Mobile: exactly 10 digits
    const phoneNoRegex = /^\d{10}$/;
    if (!phoneNoRegex.test(formData.phoneNo)) {
      setFormError("Phone number must be exactly 10 digits.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    // Password validation
    if (!formData.password || formData.password.trim() === "") {
      setFormError("Password is required");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    if (formData.password.length < 8) {
      setFormError("Password must be at least 8 characters long");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    // Password must contain at least one uppercase, one lowercase, one number
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
    if (!passwordRegex.test(formData.password)) {
      setFormError("Password must contain at least one uppercase letter, one lowercase letter, and one number");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    // Confirm password validation
    if (!formData.confirmPassword || formData.confirmPassword.trim() === "") {
      setFormError("Please confirm your password");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setFormError("Passwords do not match");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    // Aadhar: exactly 12 digits
    const aadharRegex = /^\d{12}$/;
    if (!aadharRegex.test(formData.aadharNo)) {
      setFormError("Aadhar number must be exactly 12 digits.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    // PAN: Basic format validation (10 characters, uppercase letters and digits only)
    const pancardRegex = /^[A-Z0-9]{10}$/;
    if (!pancardRegex.test(formData.pancard)) {
      setFormError("PAN card number must be 10 characters, only uppercase letters and digits");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    // DOB: must be in the past and above 13 years
    if (!formData.dateOfBirth) {
      setFormError("Date of birth is required.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    const today = new Date();
    const dob = new Date(formData.dateOfBirth);
    if (dob >= today) {
      setFormError("Date of birth must be in the past");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    const minAge = 13;
    const yearDiff = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    const dayDiff = today.getDate() - dob.getDate();
    let isAbove13 = false;
    if (yearDiff > minAge) {
      isAbove13 = true;
    } else if (yearDiff === minAge) {
      if (monthDiff > 0) {
        isAbove13 = true;
      } else if (monthDiff === 0 && dayDiff > 0) {
        isAbove13 = true;
      }
    }
    if (!isAbove13) {
      setFormError("You must be above 13 years old to register");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    // Pincode: exactly 6 digits
    const pincodeRegex = /^\d{6}$/;
    if (!pincodeRegex.test(formData.pincode)) {
      setFormError("Pincode must be exactly 6 digits.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }

    // Unique ID validation
    if (!formData.uniqueId || formData.uniqueId.trim() === "") {
      setFormError("Unique ID (Advocate) is required");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }

    // Terms
    if (!formData.termsAccepted) {
      setFormError("You must accept the terms and conditions");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError("");

    // Validate form before submission
    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        // Skip fields that shouldn't be sent to API
        if (key === 'profileImage' && formData[key]) {
          submitData.append('profileImage', formData[key]);
        } else if (key !== 'profileImage' && key !== 'confirmPassword' && key !== 'termsAccepted') {
          submitData.append(key, formData[key]);
        }
      });

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/advocate/register`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      console.log("Registration response:", response);
      // Show toast based on response status
      if (response.status === 200) {
        toast.success("OTP sent to your email. Please verify within 3 minutes.");
        setShowOtpFields(true);
        setTimer(180);
      } else if (response.status === 400) {
        toast.error(response.data.message || "User already exists. Please login.");
        setTimeout(() => {
          navigate("/advocate/login");
        }, 2000);
      } else if (response.status === 500) {
        toast.error("Internal server error. Please try again later.");
      } else {
        toast.error(response.data.message || "Registration failed. Please try again.");
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 400) {
          toast.error(error.response.data.message || "Invalid input. Please check your details.");
          if (error.response.data.message === "User already exists. Please log in.") {
            setTimeout(() => {
              navigate("/advocate/login");
            }, 2000);
          }
        } else if (error.response.status === 500) {
          toast.error("Internal server error. Please try again later.");
        } else {
          toast.error(error.response.data.message || "Registration failed. Please try again.");
        }
      } else {
        toast.error("Network error. Please try again.");
      }
    }
    setIsSubmitting(false);
  };


  // Timer effect for OTP modal
  useEffect(() => {
    if (!showOtpFields) return;
    if (timer > 0) {
      const timeout = setTimeout(() => {
        setTimer(timer - 1);
      }, 1000);
      return () => clearTimeout(timeout);
    } else {
      setCanResendOTP(true);
      setTimeout(() => window.location.reload(), 300);
    }
  }, [timer, showOtpFields]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const handleOtpChange = (e) => {
    const { name, value } = e.target;
    // Only allow numeric input for OTP fields
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    setOtpData({
      ...otpData,
      [name]: numericValue,
    });
    setFormError(""); // Clear error when user starts typing
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    // Validate OTP fields
    if (!otpData.otpEmail || otpData.otpEmail.length !== 6) {
      setFormError("Please enter a valid 6-digit email OTP");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/advocate/verifyOtp`,
        {
          phoneNo: formData.phoneNo,
          email: formData.email,
          emailOtp: otpData.otpEmail,
        }
      );
      console.log("OTP verification response:", response);

      if (response.status === 200 && response.data?.token) {
        // Save token to localStorage
        localStorage.setItem("token", response.data.token);
        toast.success("Registration successful! Redirecting to login...");
        setShowOtpFields(false);
        setTimeout(() => {
          navigate("/advocate/login");
        }, 2000);
      } else {
        setFormError(response.data?.message || "OTP verification failed");
      }
    } catch (error) {
      if (error.response?.status === 400) {
        setFormError(error.response.data?.message || "Invalid OTP. Please check and try again.");
      } else if (error.response?.status === 404) {
        setFormError("User not found or already verified. Please try registering again.");
        setTimeout(() => {
          navigate("/advocate/register");
        }, 2000);
      } else if (error.response?.status === 500) {
        toast.error("Internal server error. Please try again later.");
        setFormError("Internal server error. Please try again later.");
      } else if (error.response?.data?.message) {
        setFormError(error.response.data.message);
      } else {
        setFormError("OTP verification failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    setIsResending(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/resend-otp`,
        {
          email: formData.email,
          phoneNo: formData.phoneNo,
        }
      );

      if (response.data.success) {
        // Reset timer
        setTimer(180);
        setCanResendOTP(false);
        alert("OTP resent successfully!");
      } else {
        alert(response.data.message || "Failed to resend OTP");
      }
    } catch (error) {
      alert(error.response?.data?.message || "Failed to resend OTP");
    } finally {
      setIsResending(false);
    }
  };

  // Initialize filteredCities if editing or state is pre-filled
  useEffect(() => {
    if (formData.state && Array.isArray(stateCityData[formData.state])) {
      setFilteredCities(stateCityData[formData.state]);
    } else {
      setFilteredCities([]);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center pt-24 pb-8 px-4 sm:px-6 lg:px-8">

      {/* OTP Verification Modal */}
      {showOtpFields && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md mx-auto p-6 rounded-2xl shadow-2xl relative border border-gray-100">
            <button
              onClick={() => setShowOtpFields(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close OTP Modal"
              type="button"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Verify OTP</h3>
              <div className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                <span className="text-blue-600 font-semibold">{formatTime(timer)}</span>
              </div>
            </div>
            <form onSubmit={handleOtpSubmit} className="space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded-md text-xs font-medium">
                  {formError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email OTP</label>
                <input
                  type="text"
                  id="otpEmail"
                  name="otpEmail"
                  maxLength={6}
                  value={otpData.otpEmail}
                  onChange={handleOtpChange}
                  className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter 6-digit OTP"
                  pattern="[0-9]{6}"
                  inputMode="numeric"
                />
                <p className="mt-2 text-xs text-gray-500">Check your email {formData.email}</p>
              </div>
              <div className="flex flex-col gap-3 mt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-medium transition-all ${isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                      </svg>
                      Verifying...
                    </span>
                  ) : (
                    "Verify OTP"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={!canResendOTP || isResending}
                  className={`w-full flex justify-center py-3 px-4 border rounded-xl shadow-sm font-medium transition-all ${canResendOTP && !isResending ? "border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100" : "border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed"}`}
                >
                  {isResending ? "Resending..." : "Resend OTP"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="w-full max-w-6xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
        <div className="flex flex-col">
          {/* Form */}
          <div className="w-full p-6 lg:p-8">
            <div className="mb-4">
              <div className="text-center mb-3">
                <h1 className="text-xl font-bold mb-2">
                  <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    Advocate Registration
                  </span>
                </h1>
                <div className="w-16 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full mb-2"></div>
                <h2 className="text-base font-semibold text-gray-800 mb-1">
                  Create Account
                </h2>
                <p className="text-xs text-gray-600">Register as an advocate and join our legal community</p>
              </div>
            </div>

            {/* Registration form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded-md text-xs font-medium">
                  {formError}
                </div>
              )}

              {/* Profile Image Upload */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-600 mb-2">
                  Profile Image
                </label>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <input
                      type="file"
                      name="profileImage"
                      accept="image/*"
                      onChange={handleChange}
                      className="hidden"
                      id="profileImage"
                    />
                    <label
                      htmlFor="profileImage"
                      className="cursor-pointer flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 transition-colors bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faImage} className="text-gray-400 text-sm" />
                        <span className="text-xs text-gray-600">Choose Image</span>
                      </div>
                    </label>
                  </div>
                  {profileImagePreview && (
                    <div className="relative">
                      <img
                        src={profileImagePreview}
                        alt="Profile Preview"
                        className="w-16 h-16 object-cover rounded-lg border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, profileImage: null });
                          setProfileImagePreview(null);
                          document.getElementById('profileImage').value = '';
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">Max size: 5MB (JPG, PNG, BMP)</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {/* Name Field */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Full Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faUser} className="text-gray-400 text-xs" />
                    </div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                </div>
                {/* Gender Field */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Gender
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faVenusMars} className="text-gray-400 text-xs" />
                    </div>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all appearance-none text-sm"
                      required
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                {/* Email Field */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faEnvelope} className="text-gray-400 text-xs" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>
                {/* Phone Number Field */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Phone Number
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faPhoneAlt} className="text-gray-400 text-xs" />
                    </div>
                    <input
                      type="tel"
                      name="phoneNo"
                      value={formData.phoneNo}
                      onChange={handleChange}
                      className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                      placeholder="Enter your phone number"
                      required
                    />
                  </div>
                </div>
                {/* Password Field */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faLock} className="text-gray-400 text-xs" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="block w-full pl-8 pr-10 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="text-xs" />
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Min 8 characters, 1 uppercase, 1 lowercase, 1 number</p>
                </div>
                {/* Confirm Password Field */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faLock} className="text-gray-400 text-xs" />
                    </div>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="block w-full pl-8 pr-10 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                      placeholder="Confirm your password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} className="text-xs" />
                    </button>
                  </div>
                </div>
                {/* Aadhar Card field */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Aadhar Number
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faIdCard} className="text-gray-400 text-xs" />
                    </div>
                    <input
                      type="text"
                      name="aadharNo"
                      value={formData.aadharNo}
                      onChange={handleChange}
                      className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                      placeholder="Enter your 12-digit Aadhar number"
                      pattern="[0-9]{12}"
                      maxLength="12"
                    />
                  </div>
                </div>
                {/* PAN Card field */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    PAN Card
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faIdCard} className="text-gray-400 text-xs" />
                    </div>
                    <input
                      type="text"
                      name="pancard"
                      value={formData.pancard}
                      onChange={handleChange}
                      className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                      placeholder="Enter your PAN card number"
                    />
                  </div>
                </div>
                {/* Date of Birth field */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Date of Birth
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faCalendar} className="text-gray-400 text-xs" />
                    </div>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                    />
                  </div>
                </div>
                {/*  Address Field */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Residence Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400 text-xs" />
                    </div>
                    <input
                      type="text"
                      name="residenceAddress"
                      value={formData.residenceAddress}
                      onChange={handleChange}
                      className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                      placeholder="Enter your residence address"
                    />
                  </div>
                </div>
                {/* State Field */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    State
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400 text-xs" />
                    </div>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleStateChange}
                      className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all appearance-none text-sm"
                    >
                      <option value="">Select State</option>
                      {states.map((state, index) => (
                        <option key={index} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* City Field */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    City
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faCity} className="text-gray-400 text-xs" />
                    </div>
                    <select
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all appearance-none text-sm"
                      disabled={!formData.state}
                    >
                      <option value="">{formData.state ? "Select City" : "Select State First"}</option>
                      {filteredCities.map((city, index) => (
                        <option key={index} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* Pincode Field */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Pin Code
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleChange}
                      className="block w-full pl-2 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                      placeholder="Enter your 6-digit pincode"
                      maxLength="6"
                    />
                  </div>
                </div>
                {/* Specialization Field */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Specialization
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faBriefcase} className="text-gray-400 text-xs" />
                    </div>
                    <select
                      name="specialization"
                      value={formData.specialization}
                      onChange={handleChange}
                      className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all appearance-none text-sm"
                      required
                    >
                      <option value="">Select Specialization</option>
                      <option value="Civil law">Civil law</option>
                      <option value="Criminal law">Criminal law</option>
                      <option value="Family law">Family law</option>
                      <option value="Corporate/company law">Corporate/company law</option>
                      <option value="Tax law">Tax law</option>
                      <option value="Consumer law">Consumer law</option>
                      <option value="Cyber law">Cyber law</option>
                      <option value="IPR law">IPR law</option>
                      <option value="Labour & employment law">Labour & employment law</option>
                      <option value="Real Estate/RERA">Real Estate/RERA</option>
                      <option value="Arbitration & Mediation">Arbitration & Mediation</option>
                      <option value="Constitutional/writ practice">Constitutional/writ practice</option>
                      <option value="High court practice">High court practice</option>
                      <option value="Supreme court practice">Supreme court practice</option>
                    </select>
                  </div>
                </div>
                {/* Experience Field */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Experience
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faGraduationCap} className="text-gray-400 text-xs" />
                    </div>
                    <select
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                      className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all appearance-none text-sm"
                      required
                    >
                      <option value="">Select Experience</option>
                      {Array.from({ length: 25 }, (_, i) => i + 1).map(year => (
                        <option key={year} value={year}>{year} {year === 1 ? 'Year' : 'Years'}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* Bar Association/Court Field */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Bar Association/Court
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faGavel} className="text-gray-400 text-xs" />
                    </div>
                    <input
                      type="text"
                      name="barAssociationCourt"
                      value={formData.barAssociationCourt}
                      onChange={handleChange}
                      className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                      placeholder="Enter Bar Association/Court"
                      required
                    />
                  </div>
                </div>
                {/* Unique ID (Advocate) Field */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Unique ID (Advocate)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faIdCard} className="text-gray-400 text-xs" />
                    </div>
                    <input
                      type="text"
                      name="uniqueId"
                      value={formData.uniqueId}
                      onChange={handleChange}
                      className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                      placeholder="Enter your unique advocate ID"
                      required
                    />
                  </div>
                </div>
              </div>
              {/* Terms and Conditions */}
              <div className="flex items-start mt-4">
                <input
                  type="checkbox"
                  name="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={handleChange}
                  className="mt-1 mr-2 h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  required
                />
                <label className="text-xs text-gray-600 leading-relaxed">
                  I accept the <span
                    className="text-blue-600 hover:text-blue-700 underline font-medium cursor-pointer"
                    onClick={() => window.open('/termscondition', '_blank')}
                  >
                    terms and conditions
                  </span>
                </label>
              </div>

              <div className="mt-4">
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2.5 px-4 rounded-lg focus:ring-1 focus:ring-blue-500 focus:ring-offset-1 flex items-center justify-center font-medium transition-all hover:from-blue-700 hover:to-blue-800 shadow-md"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                      </svg>
                      Registering
                    </span>
                  ) : (
                    "Create Account"
                  )}
                </button>
              </div>

              <div className="text-center text-xs mt-3">
                <span className="text-gray-500">Already have an account? </span>
                <Link to="/advocate/login" className="text-blue-600 hover:text-blue-700 font-medium transition-colors">
                  Sign in
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvocateRegister;
